import { type ConditionalStyleSheet } from '..'
import { type EmbedCSSRule, type EmbedCSSStyles } from '../embedder/style'
import { debug } from './logger'
import { match } from './string-match'

/**
 * Wraps CSS rules under a selector.
 * if selector starts with '&', it will be replaced with parentSelector.
 * For simplicity, edge cases are not handled.
 * - `'*[data-attr="something"]' + '& div'` does not work
 * - `'*[data-attr="some,thing"]'` does not work because of comma
 * parentSelector will likely be a scope name in current usage, but it can be anything,
 * however `nanoid`, id gen we use, does not use comma so it should be fine for the most part.
 * Open for improvement.
 * @param parentSelector
 * @param rules
 * @returns
 */
export function wrapCSSRulesBySelector (parentSelector: string, rules: EmbedCSSRule[] = []): EmbedCSSRule[] {
  return rules.map(({ selector, style }) => {
    const wrapperSelector = selector.split(',').map(_s => {
      const s = _s.trim()
      if (s.startsWith('&')) {
        return s.replace(/^&/, parentSelector)
      }
      return `${parentSelector} ${s}`
    }).join(', ')
    return { selector: wrapperSelector, style }
  })
}

/**
 * Generates a CSS selector from attributes
 * @param attributes
 * @returns
 */
export function generateSelectorFromAttributes (attributes: Record<string, string>): string {
  return Object.entries(attributes)
    .map(([key, value]) => {
      const escaped = value.replace(/(["\\])/g, '\\$1')
      return `[${key}="${escaped}"]`
    })
    .join('')
}

export function injectConditionalSheet (sheet: ConditionalStyleSheet, componentSelector: string) {
  const { id, rules, attributes, type, urlMatch } = sheet
  if (rules == null) return
  if (urlMatch != null && !match(window.location.href, urlMatch)) return
  if (type === 'global') {
    injectCSSRules(rules, id, attributes)
  } else {
    const wrappedRules = wrapCSSRulesBySelector(componentSelector, rules)
    injectCSSRules(wrappedRules, id, attributes)
  }
}

export function injectCSSRules (rules: EmbedCSSRule[], id?: string, attributes?: Record<string, string>): void {
  if (id != null && (document.getElementById(id) != null)) {
    debug(`This style already exists. Skipping...: ${id}`)
    return
  }
  const styleEl = document.createElement('style')
  if (attributes != null) Object.entries(attributes).forEach(([key, value]) => { styleEl.setAttribute(key, value) })
  if (id != null) styleEl.setAttribute('id', id)
  document.head.appendChild(styleEl)
  const styleSheet = styleEl.sheet
  if (styleSheet == null) {
    throw new Error('Could not initialize stylesheet.')
  }

  for (let i = 0; i < rules.length; i++) {
    const { selector, style } = rules[i]
    const stylesText = Object.entries(style).map(([key, value]) => `${key}: ${value};`).join('')
    styleSheet.insertRule(`${selector} {${stylesText}}`, styleSheet.cssRules.length)
  }
}

export function applyInlineStyles (styles: EmbedCSSStyles, el: HTMLElement): void {
  Object.entries(styles).forEach(([key, value]) => {
    el.style.setProperty(key, value?.toString?.())
  })
}

/**
 * Normalizes CSS unit to px
 * @param value
 * @returns
 */
export function normalizeCSSUnit (value: string | number): string {
  if (typeof value === 'number') {
    return `${value}px`
  }
  return value
}

/**
 * Normalizes CSS unit to px for all values in object
 * @param styles
 * @returns
 */
export function normalizeCSSUnitObject (styles: Record<string, any>, properties: string[] = ['top', 'left', 'height', 'width', 'bottom', 'right']): EmbedCSSStyles {
  return Object.entries(styles).reduce((acc, [key, value]) => {
    if (!properties.includes(key)) {
      acc[key] = value
      return acc
    }
    acc[key] = normalizeCSSUnit(value)
    return acc
  }, {})
}

/**
 * Returns true if unit value is zero
 */
export function isZeroValue (value: string | number): boolean {
  if (typeof value === 'number') { return value === 0 }
  return /^0\w*/.test(value)
}
